import cn from 'classnames';
import { FC, RefObject, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Icon, Tabs } from '@/shared/ui';

import { ChecklistPanelTab } from '../../config';
import { ChecklistTagList } from '../ChecklistTagList';

import styles from './ChecklistPanel.module.scss';

type ChecklistPanelProps = {
  tabs: ChecklistPanelTab[];
  className?: string;
  // outside-click ignores this element, otherwise a toggle button would instantly reopen the panel
  triggerRef?: RefObject<HTMLElement>;
  onClose: () => void;
};

export const ChecklistPanel: FC<ChecklistPanelProps> = (props) => {
  const { tabs, className, triggerRef, onClose } = props;

  const { formatMessage } = useIntl();

  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        panelRef.current?.contains(target) ||
        triggerRef?.current?.contains(target)
      ) {
        return;
      }

      onClose();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, triggerRef]);

  if (tabs.length === 0) {
    return null;
  }

  return (
    <div
      ref={panelRef}
      className={cn(styles.panel, className)}
      data-section="checklist-panel"
    >
      <div className={styles.header}>
        <h3 className={cn('h6', styles.title)}>
          <FormattedMessage
            id="checklistPanel.title"
            defaultMessage="Checklist"
          />
        </h3>
        <button
          type="button"
          className={styles.closeButton}
          aria-label={formatMessage({
            id: 'checklistPanel.close',
            defaultMessage: 'Close checklist',
          })}
          onClick={onClose}
        >
          <Icon name="close" size={24} />
        </button>
      </div>

      {tabs.length === 1 ? (
        <div className={styles.body}>
          <ChecklistTagList rows={tabs[0].rows} />
        </div>
      ) : (
        <Tabs.Root defaultValue={tabs[0].id} className={styles.tabsRoot}>
          <Tabs.List size="small" className={styles.tabsList}>
            {tabs.map((tab) => (
              <Tabs.Trigger key={tab.id} value={tab.id} size="small">
                {tab.label}
              </Tabs.Trigger>
            ))}
          </Tabs.List>
          {tabs.map((tab) => (
            <Tabs.Content
              key={tab.id}
              value={tab.id}
              className={styles.tabContent}
            >
              <div className={styles.body}>
                <ChecklistTagList rows={tab.rows} />
              </div>
            </Tabs.Content>
          ))}
        </Tabs.Root>
      )}
    </div>
  );
};
